import { cloneElement, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ButtonRadio } from '@/components'
import { TeamAppContext } from '@/contexts/TeamContext'
import { Instance } from '@/models/instance'

import { InstanceAccess } from './InstanceAccess'
import { InstanceAccessWithPublicIP } from './InstanceAccessWithPublicIP'
import { InstanceBilling } from './InstanceBilling'
import { InstanceConfig } from './InstanceConfig'
import { InstanceDatadrive } from './InstanceDatadrive'
import { InstanceHardware } from './InstanceHardware'
import { InstanceMonitor } from './InstanceMonitor'
import { InstanceNetworkGroup } from './InstanceNetworkGroup'

// import { InstanceOperationLog } from './InstanceOperationLog'

interface InstanceDetailProps {
  instance: Instance
  onUpdate?: () => void
  hideMonitor?: boolean
  teamResID?: string
  readOnly?: boolean
}

type Item = {
  label: string
  value: string
  Comp: JSX.Element
}

export function InstanceDetail({
  instance,
  onUpdate,
  hideMonitor,
  teamResID,
  readOnly = false,
}: InstanceDetailProps) {
  const { t } = useTranslation()
  const teamApp = useContext(TeamAppContext)
  const isTeamContext = teamApp !== null

  const accessItem = instance.isRunning
    ? {
        label: t('instance.detail.accessTitle'),
        value: 'Access',
        Comp:
          instance.nats && instance.nats.length > 0 ? (
            <InstanceAccessWithPublicIP
              instance={instance}
              onUpdate={onUpdate}
              readOnly={readOnly}
            />
          ) : (
            <InstanceAccess instance={instance} onUpdate={onUpdate} readOnly={readOnly} />
          ),
      }
    : undefined

  const monitorItem =
    instance.isRunning && !hideMonitor
      ? {
          label: t('instance.detail.monitorTitle'),
          value: 'Monitor',
          Comp: <InstanceMonitor instance={instance} />,
        }
      : undefined

  const billingItem =
    (instance.isRunning || instance.isReleased) && instance.displayID
      ? {
          label: t('instance.detail.billingTitle'),
          value: 'Billing',
          Comp: (
            <InstanceBilling
              instance={instance}
              teamResID={teamResID}
              onInstanceUpdated={onUpdate}
            />
          ),
        }
      : undefined

  const configItem = {
    label: t('instance.detail.configTitle'),
    value: 'Config',
    Comp: <InstanceConfig instance={instance} />,
  }

  const datadriveItem = {
    label: t('instance.detail.datadriveTitle'),
    value: 'Datadrive',
    Comp: <InstanceDatadrive instance={instance} />,
  }

  const hardwareItem =
    instance.isRunning || instance.hardware
      ? {
          label: t('instance.detail.hardwareTitle'),
          value: 'Hardware',
          Comp: <InstanceHardware instance={instance} readOnly={readOnly} />,
        }
      : undefined

  const netGroupItem =
    instance.isRunning && !isTeamContext
      ? {
          label: t('instance.detail.networkTitle'),
          value: 'NetworkGroup',
          Comp: <InstanceNetworkGroup instance={instance} />,
        }
      : undefined

  const items: Item[] = [
    accessItem,
    monitorItem,
    billingItem,
    configItem,
    datadriveItem,
    hardwareItem,
    netGroupItem,
  ].filter(Boolean) as Item[]

  const [tab, setTab] = useState(items[0].value)

  return (
    <div>
      <div className="px-4">
        <ButtonRadio
          className="mb-2 "
          value={tab}
          items={items}
          onChange={(e) => setTab(e)}
          size={'small'}
        />

        {items.map((item) => tab === item.value && cloneElement(item.Comp, { key: item.value }))}
      </div>
    </div>
  )
}
